import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getProduct, getProducts, addReview, toggleWishlist } from '../utils/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useSEO } from '../utils/useSEO';
import { formatDate } from '../utils/helpers';
import ProductCard from '../components/common/ProductCard';
import {
  FiShoppingCart, FiHeart, FiStar, FiTruck, FiShield,
  FiRefreshCw, FiMinus, FiPlus, FiChevronRight, FiX,
  FiCheckCircle, FiZoomIn, FiShare2,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import styles from './ProductDetailPage.module.css';

const WHATSAPP_NUMBER  = '8801XXXXXXXXX';
const MESSENGER_PAGE   = 'bdshop';

const PAYMENT_METHODS = [
  { id:'bkash',      icon:'📱', label:'bKash',      color:'#E2136E' },
  { id:'nagad',      icon:'💰', label:'Nagad',      color:'#F6821F' },
  { id:'rocket',     icon:'🚀', label:'Rocket',     color:'#8B5CF6' },
  { id:'sslcommerz', icon:'💳', label:'Card',       color:'#2563EB' },
  { id:'cod',        icon:'💵', label:'COD',        color:'#16A34A' },
];

export default function ProductDetailPage() {
  const { id }      = useParams();
  const { addItem } = useCart();
  const { user }    = useAuth();
  const navigate    = useNavigate();
  const reviewsRef  = useRef();

  const [product, setProduct]       = useState(null);
  const [related, setRelated]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [mainImg, setMainImg]       = useState(0);
  const [qty, setQty]               = useState(1);
  const [selVars, setSelVars]       = useState({});
  const [wishlisted, setWishlisted] = useState(false);
  const [activeTab, setActiveTab]   = useState('description');
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const [zoomOpen, setZoomOpen]     = useState(false);
  const [shareOpen, setShareOpen]   = useState(false);

  useSEO({
    title:       product?.name,
    description: product?.shortDescription || product?.description?.substring(0, 160),
    image:       product?.thumbnail,
  });

  useEffect(() => {
    setLoading(true); setMainImg(0); setQty(1); setSelVars({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
    getProduct(id)
      .then(r => {
        setProduct(r.data);
        if (r.data.category?._id) {
          getProducts({ category: r.data.category._id, limit: 6 })
            .then(rel => setRelated(rel.data.products.filter(p => p._id !== r.data._id).slice(0, 5)));
        }
      })
      .catch(() => toast.error('Product not found'))
      .finally(() => setLoading(false));
  }, [id]);

  // Close share dropdown when clicking outside
  useEffect(() => {
    if (!shareOpen) return;
    const fn = () => setShareOpen(false);
    document.addEventListener('click', fn);
    return () => document.removeEventListener('click', fn);
  }, [shareOpen]);

  const handleAddToCart = () => {
    const varStr = Object.entries(selVars).map(([k,v]) => `${k}: ${v}`).join(', ');
    addItem(product, qty, varStr);
    toast.success(`🛒 ${qty}× ${product.name.slice(0,30)}… added!`);
  };

  const handleBuyNow = () => {
    const varStr = Object.entries(selVars).map(([k,v]) => `${k}: ${v}`).join(', ');
    addItem(product, qty, varStr);
    navigate('/checkout');
  };

  const handleWishlist = async () => {
    if (!user) { toast.error('Please login first'); return; }
    await toggleWishlist(product._id);
    setWishlisted(w => !w);
    toast.success(wishlisted ? 'Removed from wishlist' : '❤️ Added to wishlist');
  };

  const handleReview = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please login to review'); return; }
    setSubmitting(true);
    try {
      await addReview(product._id, reviewForm);
      toast.success('✅ Review submitted!');
      const r = await getProduct(id);
      setProduct(r.data);
      setReviewForm({ rating: 5, comment: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSubmitting(false); }
  };

  // Share handlers
  const shareUrl  = window.location.href;
  const shareText = product ? `Check out ${product.name} on BDShop! ৳${(product.discountPrice || product.price)?.toLocaleString()}` : '';

  const shareWhatsApp = () => {
    const waMsg = product
      ? `Hi! I found this on BDShop:\n*${product.name}*\nPrice: ৳${(product.discountPrice || product.price)?.toLocaleString()}\n${shareUrl}`
      : shareText;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waMsg)}`, '_blank');
    setShareOpen(false);
  };

  const shareMessenger = () => {
    window.open(`https://m.me/${MESSENGER_PAGE}?ref=${encodeURIComponent(shareUrl)}`, '_blank');
    setShareOpen(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied!');
    setShareOpen(false);
  };

  if (loading) return (
    <div className="page-loader">
      <div className="spinner" style={{ width: 52, height: 52 }} />
      <p style={{ color: 'var(--text-muted)', marginTop: 8 }}>Loading product…</p>
    </div>
  );
  if (!product) return (
    <div className="page-loader">
      <span style={{ fontSize: '3rem' }}>😕</span>
      <h2>Product not found</h2>
      <Link to="/products" className="btn btn-primary" style={{ marginTop: 12 }}>Browse Products</Link>
    </div>
  );

  const images   = product.images?.length ? product.images : [product.thumbnail || 'https://via.placeholder.com/600'];
  const price    = product.discountPrice || product.price;
  const discount = product.discountPrice
    ? Math.round(((product.price - product.discountPrice) / product.price) * 100) : 0;

  return (
    <div itemScope itemType="https://schema.org/Product">

      {/* Breadcrumb */}
      <div className={styles.breadBar}>
        <div className="container">
          <nav className={styles.breadcrumb} aria-label="Breadcrumb">
            <Link to="/">Home</Link><FiChevronRight size={11}/>
            <Link to="/products">Products</Link>
            {product.category && (
              <><FiChevronRight size={11}/>
              <Link to={`/products?category=${product.category._id}`}>{product.category.name}</Link></>
            )}
            <FiChevronRight size={11}/>
            <span>{product.name}</span>
          </nav>
        </div>
      </div>

      <div className={`container ${styles.mainWrap}`}>
        <div className={styles.grid}>

          {/* ── IMAGE GALLERY ── */}
          <div className={styles.gallerySection}>
            <div className={styles.galleryInner}>
              {/* Desktop thumbnails */}
              {images.length > 1 && (
                <div className={styles.thumbCol}>
                  {images.map((img, i) => (
                    <button key={i} className={`${styles.thumb} ${i===mainImg?styles.thumbActive:''}`} onClick={() => setMainImg(i)}>
                      <img src={img} alt="" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}

              {/* Main image */}
              <div className={styles.mainImgBox} onClick={() => setZoomOpen(true)}>
                <img src={images[mainImg]} alt={product.name} className={styles.mainImg} itemProp="image" />
                {discount >= 5 && <span className={styles.discTag}>-{discount}%</span>}
                {product.stock === 0 && <div className={styles.outOfStock}>Out of Stock</div>}
                <button className={styles.zoomBtn} title="Zoom"><FiZoomIn size={14}/></button>
                {images.length > 1 && (
                  <div className={styles.dots}>
                    {images.map((_, i) => (
                      <button key={i} className={`${styles.dot} ${i===mainImg?styles.dotActive:''}`}
                        onClick={e => { e.stopPropagation(); setMainImg(i); }} />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile thumbnail strip */}
            {images.length > 1 && (
              <div className={styles.thumbStrip}>
                {images.map((img, i) => (
                  <button key={i} className={`${styles.thumb} ${i===mainImg?styles.thumbActive:''}`} onClick={() => setMainImg(i)}>
                    <img src={img} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── PRODUCT INFO ── */}
          <div className={styles.infoSection}>
            <div className={styles.topBadges}>
              {product.brand && <span className={styles.brandTag}>{product.brand}</span>}
              {product.isFeatured && <span className="badge badge-warning">⭐ Featured</span>}
              {product.stock > 0 && product.stock <= 5 && (
                <span className="badge badge-error">🔥 Only {product.stock} left</span>
              )}
            </div>

            <h1 className={styles.title} itemProp="name">{product.name}</h1>

            {/* Rating row */}
            <div className={styles.ratingRow}>
              <div className={styles.stars}>
                {[1,2,3,4,5].map(s => (
                  <FiStar key={s} size={14}
                    fill={s<=Math.round(product.rating)?'#f59e0b':'none'}
                    color={s<=Math.round(product.rating)?'#f59e0b':'#e5e7eb'}
                  />
                ))}
              </div>
              <span className={styles.ratingNum}>{product.rating?.toFixed(1)}</span>
              <button className={styles.reviewLink}
                onClick={() => { setActiveTab('reviews'); reviewsRef.current?.scrollIntoView({ behavior:'smooth', block:'start' }); }}>
                {product.numReviews} reviews
              </button>
              <span className={styles.soldText}>• {product.soldCount || 0} sold</span>

              {/* Share dropdown */}
              <div className={styles.shareWrap} onClick={e => e.stopPropagation()}>
                <button className={styles.shareBtn} onClick={() => setShareOpen(o => !o)}>
                  <FiShare2 size={13}/> Share
                </button>
                {shareOpen && (
                  <div className={styles.shareDropdown}>
                    <button className={styles.shareItem} onClick={shareWhatsApp}>
                      <span className={styles.shareIcon} style={{ background:'#25D366' }}>
                        <svg viewBox="0 0 24 24" fill="#fff" width="14" height="14"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      </span>
                      Share on WhatsApp
                    </button>
                    <button className={styles.shareItem} onClick={shareMessenger}>
                      <span className={styles.shareIcon} style={{ background:'#0084FF' }}>
                        <svg viewBox="0 0 24 24" fill="#fff" width="14" height="14"><path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.654V24l4.088-2.242c1.092.3 2.246.464 3.443.464 6.627 0 12-4.975 12-11.111C24 4.974 18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.131 3.259L19.752 8l-6.561 6.963z"/></svg>
                      </span>
                      Share on Messenger
                    </button>
                    <button className={styles.shareItem} onClick={copyLink}>
                      <span className={styles.shareIcon} style={{ background:'#6b7280' }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" width="14" height="14"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                      </span>
                      Copy Link
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Price */}
            <div className={styles.priceArea}>
              <span className={styles.bigPrice} itemProp="price">৳{price.toLocaleString()}</span>
              {product.discountPrice && <span className={styles.strikePrice}>৳{product.price.toLocaleString()}</span>}
              {discount >= 5 && (
                <span className={styles.savePill}>Save ৳{(product.price - product.discountPrice).toLocaleString()}</span>
              )}
            </div>

            {product.shortDescription && (
              <p className={styles.shortDesc}>{product.shortDescription}</p>
            )}
            <hr className={styles.divider} />

            {/* Variants */}
            {product.variants?.map(v => (
              <div key={v.name} className={styles.variantBlock}>
                <p className={styles.varLabel}>{v.name}: {selVars[v.name] && <strong>{selVars[v.name]}</strong>}</p>
                <div className={styles.varOptions}>
                  {v.options.map(opt => (
                    <button key={opt}
                      className={`${styles.varBtn} ${selVars[v.name]===opt?styles.varBtnActive:''}`}
                      onClick={() => setSelVars(p => ({ ...p, [v.name]: opt }))}>
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            {/* Quantity */}
            <div className={styles.qtyBlock}>
              <span className={styles.varLabel}>Quantity:</span>
              <div className={styles.qtyRow}>
                <div className={styles.qtyCtrl}>
                  <button onClick={() => setQty(q => Math.max(1, q-1))}><FiMinus size={13}/></button>
                  <span className={styles.qtyNum}>{qty}</span>
                  <button onClick={() => setQty(q => Math.min(product.stock || 99, q+1))}><FiPlus size={13}/></button>
                </div>
                <span className={styles.stockLabel}>
                  {product.stock > 0
                    ? <><FiCheckCircle size={13} color="var(--success)"/> {product.stock} in stock</>
                    : <span style={{ color:'var(--error)' }}>Out of stock</span>
                  }
                </span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className={styles.ctaBtns}>
              <button className={`btn btn-xl btn-primary ${styles.addBtn}`}
                onClick={handleAddToCart} disabled={product.stock===0}>
                <FiShoppingCart size={18}/> Add to Cart
              </button>
              <button className={`btn btn-xl btn-accent ${styles.buyBtn}`}
                onClick={handleBuyNow} disabled={product.stock===0}>
                Buy Now
              </button>
              <button className={`${styles.wishBtn} ${wishlisted?styles.wishActive:''}`}
                onClick={handleWishlist} title="Wishlist" aria-label="Add to wishlist">
                <FiHeart size={19} fill={wishlisted?'#fff':'none'}/>
              </button>
            </div>

            {/* WhatsApp order button */}
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi! I want to order:\n*${product.name}*\nPrice: ৳${price.toLocaleString()}\n${window.location.href}`)}`}
              target="_blank" rel="noopener noreferrer"
              className={styles.waOrderBtn}
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Order via WhatsApp
            </a>

            {/* Delivery info */}
            <div className={styles.deliveryBox}>
              <div className={styles.delivItem}><FiTruck size={16} color="var(--primary)"/>
                <div><p>Free delivery inside Dhaka on orders ৳999+</p><p>Outside Dhaka: 2–4 days (৳120)</p></div>
              </div>
              <div className={styles.delivItem}><FiRefreshCw size={16} color="var(--primary)"/>
                <div><p>7-day easy return policy</p></div>
              </div>
              <div className={styles.delivItem}><FiShield size={16} color="var(--primary)"/>
                <div><p>100% genuine product guarantee</p></div>
              </div>
            </div>

            {/* Payment methods */}
            <div className={styles.payRow}>
              <span className={styles.payLabel}>We Accept:</span>
              <div className={styles.payIcons}>
                {PAYMENT_METHODS.map(m => (
                  <span key={m.id} className={styles.payBadge}
                    style={{ background:`${m.color}18`, color:m.color, border:`1px solid ${m.color}30` }}>
                    {m.icon} {m.label}
                  </span>
                ))}
              </div>
            </div>

            {/* Meta */}
            <div className={styles.metaInfo}>
              {product.sku && <span>SKU: <strong>{product.sku}</strong></span>}
              {product.category && <span>Category: <Link to={`/products?category=${product.category._id}`}>{product.category.name}</Link></span>}
              {product.brand && <span>Brand: <strong>{product.brand}</strong></span>}
            </div>
          </div>
        </div>

        {/* ── TABS ── */}
        <div className={styles.tabsCard} ref={reviewsRef}>
          <div className={styles.tabHeader} role="tablist">
            {[
              { key:'description', label:'📄 Description' },
              { key:'reviews',     label:`⭐ Reviews (${product.numReviews})` },
              { key:'shipping',    label:'🚚 Shipping' },
              { key:'warranty',    label:'🛡️ Warranty' },
            ].map(t => (
              <button key={t.key} role="tab" aria-selected={activeTab===t.key}
                className={`${styles.tabBtn} ${activeTab===t.key?styles.tabActive:''}`}
                onClick={() => setActiveTab(t.key)}>
                {t.label}
              </button>
            ))}
          </div>

          <div className={styles.tabBody} role="tabpanel">
            {activeTab==='description' && (
              <div className={styles.descBody}>
                <p>{product.description}</p>
                {product.tags?.length > 0 && (
                  <div className={styles.tagList}>
                    {product.tags.map(t => <span key={t} className={styles.metaTag}>{t}</span>)}
                  </div>
                )}
              </div>
            )}

            {activeTab==='reviews' && (
              <div className={styles.reviewsBody}>
                {product.numReviews > 0 && (
                  <div className={styles.ratingSummary}>
                    <span className={styles.bigRating}>{product.rating?.toFixed(1)}</span>
                    <div>
                      <div className={styles.stars}>{[1,2,3,4,5].map(s=><FiStar key={s} size={18} fill={s<=Math.round(product.rating)?'#f59e0b':'none'} color={s<=Math.round(product.rating)?'#f59e0b':'#e5e7eb'}/>)}</div>
                      <p style={{fontSize:'.8rem',color:'var(--text-muted)'}}>{product.numReviews} reviews</p>
                    </div>
                  </div>
                )}
                {product.reviews?.length === 0 && <p className={styles.noReviews}>No reviews yet. Be the first!</p>}
                <div className={styles.reviewList}>
                  {product.reviews?.map((r,i) => (
                    <div key={i} className={styles.reviewCard}>
                      <div className={styles.reviewTop}>
                        <div className={styles.reviewAvatar}>{r.name?.[0]}</div>
                        <div><p className={styles.reviewName}>{r.name}</p><p className={styles.reviewDate}>{formatDate(r.createdAt)}</p></div>
                        <div className={styles.reviewStars}>{[1,2,3,4,5].map(s=><FiStar key={s} size={11} fill={s<=r.rating?'#f59e0b':'none'} color={s<=r.rating?'#f59e0b':'#e5e7eb'}/>)}</div>
                      </div>
                      <p className={styles.reviewComment}>{r.comment}</p>
                    </div>
                  ))}
                </div>
                {user ? (
                  <form className={styles.reviewForm} onSubmit={handleReview}>
                    <h4>Write a Review</h4>
                    <div className={styles.starPicker}>
                      <span>Rating:</span>
                      {[1,2,3,4,5].map(s => (
                        <button key={s} type="button" onClick={() => setReviewForm(f=>({...f,rating:s}))}>
                          <FiStar size={22} fill={s<=reviewForm.rating?'#f59e0b':'none'} color={s<=reviewForm.rating?'#f59e0b':'#d1d5db'}/>
                        </button>
                      ))}
                    </div>
                    <textarea className="form-input" rows={3} placeholder="Share your experience…"
                      value={reviewForm.comment} onChange={e=>setReviewForm(f=>({...f,comment:e.target.value}))} required />
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting?<><div className="spinner spinner-sm"/>Submitting…</>:'Submit Review'}
                    </button>
                  </form>
                ) : (
                  <p className={styles.loginNote}>
                    Please <Link to="/login">login</Link> to write a review.
                  </p>
                )}
              </div>
            )}

            {activeTab==='shipping' && (
              <div className={styles.shippingGrid}>
                {[
                  {icon:'🏙️',title:'Inside Dhaka',      time:'Same / Next day',          price:'৳60 (Free on ৳999+)'},
                  {icon:'🚀',title:'Outside Dhaka',     time:'2–4 business days',        price:'৳120'},
                  {icon:'📦',title:'Courier Partners',  time:'SA Paribahan, Sundarban',  price:''},
                  {icon:'🔄',title:'Easy Returns',      time:'7 days from delivery',     price:'Free for defects'},
                ].map(s => (
                  <div key={s.title} className={styles.shippingCard}>
                    <span className={styles.shippingIcon}>{s.icon}</span>
                    <div><p className={styles.shippingTitle}>{s.title}</p><p style={{fontSize:'.8rem',color:'var(--text-muted)'}}>{s.time}</p>
                    {s.price&&<p style={{color:'var(--primary)',fontWeight:700,fontSize:'.82rem'}}>{s.price}</p>}</div>
                  </div>
                ))}
              </div>
            )}

            {activeTab==='warranty' && (
              <div className={styles.descBody}>
                {product.warranty
                  ? <p>{product.warranty}</p>
                  : <p style={{color:'var(--text-muted)'}}>No warranty information provided.</p>
                }
                <div className={styles.returnBox}>
                  <h4>🔄 Return Policy</h4>
                  <p>• 7-day return from delivery date</p>
                  <p>• Product must be unused in original packaging</p>
                  <p>• Contact support to initiate a return</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related products */}
        {related.length > 0 && (
          <section className={styles.relatedSection}>
            <div className="section-header">
              <h2 className="section-title">You May Also <span>Like</span></h2>
              {product.category && (
                <Link to={`/products?category=${product.category._id}`} className="see-all-btn">
                  See All <FiChevronRight size={13}/>
                </Link>
              )}
            </div>
            <div className="products-grid">
              {related.map(p => <ProductCard key={p._id} product={p}/>)}
            </div>
          </section>
        )}

        {/* Spacer for sticky bar on mobile */}
        <div style={{ height: 80 }} className="hide-desktop" aria-hidden="true" />
      </div>

      {/* Sticky mobile bottom bar */}
      <div className="sticky-atc">
        <div className={styles.stickyInfo}>
          <img src={images[0]} alt="" className={styles.stickyImg}/>
          <div>
            <p className={styles.stickyName}>{product.name.length > 28 ? product.name.slice(0,28)+'…' : product.name}</p>
            <p className={styles.stickyPrice}>৳{price.toLocaleString()}</p>
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleAddToCart} disabled={product.stock===0} style={{ flex:1 }}>
          <FiShoppingCart size={14}/> Add to Cart
        </button>
        <button className="btn btn-accent" onClick={handleBuyNow} disabled={product.stock===0} style={{ flex:1 }}>
          Buy Now
        </button>
      </div>

      {/* Zoom modal */}
      {zoomOpen && (
        <div className={styles.zoomModal} onClick={() => setZoomOpen(false)} role="dialog" aria-modal="true">
          <img src={images[mainImg]} alt={product.name} className={styles.zoomImg} />
          <button className={styles.zoomClose} onClick={() => setZoomOpen(false)}><FiX size={18}/></button>
          {images.length > 1 && (
            <div className={styles.zoomDots}>
              {images.map((_,i) => (
                <button key={i} className={`${styles.dot} ${i===mainImg?styles.dotActive:''}`}
                  onClick={e => { e.stopPropagation(); setMainImg(i); }} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
